import React, { lazy, Suspense } from 'react';
import { usePageTemplate } from '../hooks/usePageTemplate';

// Lazy load heavy components
const LazyGallery = lazy(() => import('./UnifiedGallery'));
const LazyExportControls = lazy(() => import('./ExportShareControls'));

/**
 * Standardized page template component that enforces consistent layout patterns
 * @param {Object} props - Component props
 * @param {string} props.title - Page title
 * @param {string} [props.description] - Page description
 * @param {string} [props.icon] - FontAwesome class name
 * @param {string} [props.iconImage] - Image path (legacy support)
 * @param {'default'|'narrow'|'wide'|'full'} [props.maxWidth='default'] - Container max width
 * @param {'default'|'none'|'large'} [props.padding='default'] - Container padding
 * @param {Array} [props.actions] - Header action buttons
 * @param {React.ReactNode} props.children - Page content
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.enableLazyLoading=true] - Enable lazy loading for heavy components
 * @returns {React.ReactElement} Standardized page layout
 */
const PageTemplate = ({
  title,
  description,
  icon,
  iconImage,
  maxWidth = 'default',
  padding = 'default',
  actions = [],
  children,
  className = '',
  enableLazyLoading = true,
  ...otherProps
}) => {
  const {
    containerClasses,
    headerProps,
    getIconElement
  } = usePageTemplate({
    title,
    description,
    icon,
    iconImage,
    maxWidth,
    padding,
    actions
  });

  const iconElement = getIconElement();

  // Loading fallback component
  const LoadingFallback = ({ componentName }) => (
    <div className="loading-container">
      <div className="loading-spinner">
        <i className="fas fa-spinner fa-spin"></i>
      </div>
      <p>Loading {componentName}...</p>
    </div>
  );

  // Wrap children with lazy loading if enabled
  const renderChildren = () => {
    if (!enableLazyLoading) {
      return children;
    }

    // Check if children contain heavy components and wrap them
    return React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        // Lazy load UnifiedGallery components
        if (child.type?.name === 'UnifiedGallery' || child.props?.className?.includes('unified-gallery')) {
          return (
            <Suspense fallback={<LoadingFallback componentName="Gallery" />}>
              <LazyGallery {...child.props} />
            </Suspense>
          );
        }
        
        // Lazy load ExportShareControls components
        if (child.type?.name === 'ExportShareControls' || child.props?.className?.includes('export-controls')) {
          return (
            <Suspense fallback={<LoadingFallback componentName="Export Controls" />}>
              <LazyExportControls {...child.props} />
            </Suspense>
          );
        }
      }
      return child;
    });
  };

  return (
    <div className={`${containerClasses} ${className}`} {...otherProps}>
      <div className="page-header">
        <h1>
          {iconElement && (
            iconElement.tag === 'i' ? (
              <i className={iconElement.className}></i>
            ) : (
              <img 
                src={iconElement.src} 
                alt="" 
                className={iconElement.className}
              />
            )
          )}
          {title}
        </h1>
        {description && <p>{description}</p>}
        {actions.length > 0 && (
          <div className="page-header-actions">
            {actions.map((action, index) => (
              <button
                key={index}
                className={action.className}
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.icon && <i className={action.icon}></i>}
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="page-content">
        {renderChildren()}
      </div>
    </div>
  );
};

export default PageTemplate;